import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import InterestPopup from './components/common/InterestPopup';
import BackToTop from './components/common/BackToTop';
import './App.css';
import ResilientHorizons from './pages/ResilientHorizons';

const Home = React.lazy(() => import('./pages/Home'));
const About = React.lazy(() => import('./pages/About'));
const Services = React.lazy(() => import('./pages/Services'));
const Consultation = React.lazy(() => import('./pages/Consultation'));
const Education = React.lazy(() => import('./pages/Education'));
const Events = React.lazy(() => import('./pages/Events'));
const Donate = React.lazy(() => import('./pages/Donate'));
const Courses = React.lazy(() => import('./pages/Courses'));
const Signup = React.lazy(() => import('./pages/Signup'));
//const Team = React.lazy(() => import('./pages/Team'));
const BoardMembers = React.lazy(() => import('./pages/BoardMembers'));
const Insights = React.lazy(() => import('./pages/Insights'));
const InsightArticle = React.lazy(() => import('./pages/InsightArticle'));
const Contact = React.lazy(() => import('./pages/Contact'));
const VolunteerOpportunities = React.lazy(() => import('./pages/VolunteerOpportunities'));
const VolunteerRole = React.lazy(() => import('./pages/VolunteerRole'));
const Volunteer = React.lazy(() => import('./pages/Volunteer'));
const CourseDetail = React.lazy(() => import('./pages/CourseDetail'));
const EventDetail = React.lazy(() => import('./pages/EventDetail'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

const LoadingSpinner = () => (
  <div className="flex h-64 items-center justify-center">
    <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-500"></div>
  </div>
);

const PageWrapper = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.4 }}
    className="t-12 md:pt-16"
  >
    {children}
  </motion.div>
);

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {[
          ['/', Home],
          ['/about', About],
          ['/services', Services],
          ['/consultation', Consultation],
          ['/education', Education],
          ['/resilient-horizons', ResilientHorizons],
          ['/events', Events],
          ['/donate', Donate],
          ['/courses', Courses],
          ['/courses/:slug', CourseDetail],
          ['/signup', Signup],
          //['/team', Team],
          ['/board-members', BoardMembers],
          ['/insights', Insights],
          ['/insights/:slug', InsightArticle],
          ['/resources', Insights],
          ['/resources/:slug', InsightArticle],
          ['/contact', Contact],
          ['/volunteer-opportunities', VolunteerOpportunities],
          ['/volunteer-opportunities/:slug', VolunteerRole],
          ['/volunteer', Volunteer],
          ['/events/:id', EventDetail],
          ['/privacy-policy', PrivacyPolicy]
        ].map(([path, Component]) => (
          <Route
            key={path}
            path={path}
            element={
              <PageWrapper>
                <Suspense fallback={<LoadingSpinner />}>
                  <Component />
                </Suspense>
              </PageWrapper>
            }
          />
        ))}
        <Route
          path="*"
          element={
            <PageWrapper>
              <Suspense fallback={<LoadingSpinner />}>
                <NotFound />
              </Suspense>
            </PageWrapper>
          }
        />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <div className="App flex min-h-screen flex-col bg-white">
        <Header />
        <main className="flex-grow">
          <AnimatedRoutes />
        </main>
        <Footer />
        <InterestPopup />
        <BackToTop />
      </div>
    </Router>
  );
}

export default App;
